/**
 * Feature Flags Service
 * Fetches and caches feature flags from app_settings table
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from './logger';

const CACHE_KEY = 'feature_flags_cache';
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

interface CachedFlags {
  flags: Record<string, string>;
  timestamp: number;
}

/**
 * Get cached flags from localStorage
 */
function getCachedFlags(): Record<string, string> | null {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const parsed: CachedFlags = JSON.parse(cached);
    const now = Date.now();

    // Check if cache is still valid
    if (now - parsed.timestamp < CACHE_TTL_MS) {
      return parsed.flags;
    }

    // Cache expired
    return null;
  } catch {
    return null;
  }
}

/**
 * Save flags to localStorage cache
 */
function setCachedFlags(flags: Record<string, string>): void {
  try {
    const cacheEntry: CachedFlags = {
      flags,
      timestamp: Date.now(),
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheEntry));
  } catch {
    // Ignore localStorage errors
  }
}

/**
 * Fetch all feature flags from database
 */
async function fetchFlags(): Promise<Record<string, string>> {
  try {
    const { data, error } = await supabase
      .from('app_settings')
      .select('key, value');

    if (error) {
      logger.error('Failed to fetch feature flags', {
        context: { feature: 'feature_flags', function_name: 'fetchFlags' },
        error,
        error_code: 'feature_flags_fetch_failed',
        error_type: 'network',
      });
      return {};
    }

    const flags: Record<string, string> = {};
    const rows = data as Array<{ key: string; value: string | null }> || [];
    for (const row of rows) {
      if (row.key && row.value !== null) {
        flags[row.key] = row.value;
      }
    }

    return flags;
  } catch (e) {
    logger.error('Error fetching feature flags', {
      context: { feature: 'feature_flags', function_name: 'fetchFlags' },
      error: e,
      error_code: 'feature_flags_fetch_failed',
      error_type: 'network',
    });
    return {};
  }
}

export const featureFlagsService = {
  /**
   * Get all feature flags (from cache or fetch if expired/empty)
   */
  async getFlags(): Promise<Record<string, string>> {
    // Try cache first
    const cached = getCachedFlags();
    if (cached) {
      return cached;
    }

    // Fetch from database
    const flags = await fetchFlags();
    setCachedFlags(flags);
    return flags;
  },

  /**
   * Get a specific feature flag value
   * @param key - The flag key
   * @param defaultValue - Default value if flag not found
   */
  async getFlag(key: string, defaultValue: string = 'false'): Promise<string> {
    const flags = await this.getFlags();
    return flags[key] ?? defaultValue;
  },

  /**
   * Get a boolean feature flag
   * @param key - The flag key
   * @param defaultValue - Default value if flag not found
   */
  async isEnabled(key: string, defaultValue: boolean = false): Promise<boolean> {
    const value = await this.getFlag(key, defaultValue ? 'true' : 'false');
    return value === 'true';
  },

  /**
   * Clear the cache (useful for testing or forcing refresh)
   */
  clearCache(): void {
    try {
      localStorage.removeItem(CACHE_KEY);
    } catch {
      // Ignore localStorage errors
    }
  },

  /**
   * Force refresh flags from database
   */
  async refreshFlags(): Promise<Record<string, string>> {
    this.clearCache();
    return this.getFlags();
  },
};

export default featureFlagsService;
